import {TableBody, TableHead, TextField, Typography} from "@material-ui/core";
import React from "react";
import {TableSortLabel} from "@material-ui/core";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import styles from "../../pages/Dashboard/DashboardFinal/DashboardFinal.module.css";
import "./ShipmentSummary.css";
import "../../pages/Dashboard/DashboardSummary.css";
import { REPORT_TITLE } from "../../pages/Dashboard/DashboardFinal/constants";
import "./ShipmentForm.css";
import SearchableTable from "../global/Search/SearchableTable";
import {TableFooter} from '@material-ui/core';
import Autocomplete from "@material-ui/lab/Autocomplete";
import withShipment from "../../withShipment";
import {updateUserInfo} from "../../redux/actions/auth";
import {selectedLocationIdSelector, userInfoSelector} from "../../redux/selectors/auth";
import PropTypes from "prop-types";
import LocationSelectBox from "../global/subcomponents/locationSelectBox/LocationSelectBox";
import {SHIPPED} from "../stages/constants";

// product codes look like "(SHORTCODE)-N"; brand is per shortcode regardless of the pack size
const getShortcode = (productCode) => productCode.match(/\(([^)]+)\)/)?.[1] ?? productCode

const getProductCodes = (pkg) => (pkg.reference3?.split('/') ?? []).map(code => code?.trim()).filter(Boolean)

/**
 * Builds the summary report rows ({brand, productCode, printedQty}) from the given shipments, sorted by brand,
 * then shortcode, then the number of packages (most first). The key order is also the CSV column order.
 *
 * A package only carries a de-duplicated set of brands, not a brand per product, so the brand of a
 * shortcode is taken from packages that have a single brand
 */
export const buildShipmentSummaryReport = (rows) => {
    const brandByShortcode = {}
    rows.forEach(row => row.packages?.forEach(pkg => {
        const brands = [...new Set((pkg.brands ?? []).filter(Boolean))]
        if (brands.length === 1) {
            getProductCodes(pkg).forEach(code => {
                const shortcode = getShortcode(code)
                if (!brandByShortcode[shortcode]) {
                    brandByShortcode[shortcode] = brands[0]
                }
            })
        }
    }))

    const reportsByProductCode = {}
    rows.forEach(row => row.packages?.forEach(pkg => {
        getProductCodes(pkg).forEach(code => {
            if (reportsByProductCode[code]) {
                reportsByProductCode[code].printedQty++
            } else {
                reportsByProductCode[code] = {
                    brand: brandByShortcode[getShortcode(code)] ?? "",
                    productCode: code,
                    printedQty: 1
                }
            }
        })
    }))

    const compareText = (a, b) => a.toLowerCase().localeCompare(b.toLowerCase())
    return Object.values(reportsByProductCode).sort((a, b) =>
        compareText(a.brand, b.brand)
        || compareText(getShortcode(a.productCode), getShortcode(b.productCode))
        || b.printedQty - a.printedQty
        || compareText(a.productCode, b.productCode)
    )
}

class ShipmentsSummary extends SearchableTable {
    constructor(props) {
        super(props);
        this.state = {
            missingScanned: [],
            missingPacked: [],
            reportData: [],
            // most packages first; ties keep the report's brand / shortcode order (stable sort)
            valueToOrderByShipmentsSummary: "printedQty",
            orderDirectionShipmentsSummary: "desc",
            selectedLocation: {},
            rows: []
        }
    }

    componentDidMount() {
        //filter out the shipment that does not have shipment status for shipped shipment
        this.state.rows = this.props.allRows.filter(row => row.shipAddressId === this.props.selectedLocationId
            && (
                this.props.shipmentStatus !== SHIPPED || row.hasShipmentStatus
            ))

        this.state.selectedLocation = this.props.locations.find(location =>
            location.shipAddressId === this.props.selectedLocationId)

        this.gatherPrintedPendingReport();
        this.props.updateExportInfo(this.state.rows)
        // the CSV export sorts by the stored order, so keep it in line with what this table shows
        this.props.setShipmentSummaryOrderDirection(this.state.orderDirectionShipmentsSummary, this.state.valueToOrderByShipmentsSummary)
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        //filter out the shipment that does not have shipment status for shipped shipment
        this.state.rows = this.props.allRows.filter(row => row.shipAddressId === this.props.selectedLocationId
            && (
                this.props.shipmentStatus !== SHIPPED || row.hasShipmentStatus
            ))

        // rebuild the report when the shipments are refreshed or the location changes
        if (prevProps.allRows !== this.props.allRows || prevProps.selectedLocationId !== this.props.selectedLocationId) {
            this.state.selectedLocation = this.props.locations.find(location =>
                location.shipAddressId === this.props.selectedLocationId)
            this.gatherPrintedPendingReport();
            this.props.updateExportInfo(this.state.rows)
        }
    }

    gatherPrintedPendingReport = () => {
        this.setState({
            reportData: buildShipmentSummaryReport(this.state.rows)
        })
    }

    createSortHandler = (property) => (event) => {
        this.handleRequestSort(event, property);
    }

    handleRequestSort = (event, property) => {
        const isAscendingShipments = (this.state.valueToOrderByShipmentsSummary === property && this.state.orderDirectionShipmentsSummary === "asc");
        this.setState({
            valueToOrderByShipmentsSummary: property,
            orderDirectionShipmentsSummary: (isAscendingShipments ? 'desc' : 'asc')
        })
        this.props.setShipmentSummaryOrderDirection(isAscendingShipments ? 'desc' : 'asc', property)
    }

    addItUp = (addOn) => {
        let summer = 0;
        this.state.reportData.forEach(entry => {
            summer += entry[addOn];
        })
        return summer;
    }

    locationSelectRow = () => (
        <div className={styles.dateselectrow}>

            <div style={{width: "auto"}}></div>

            <div className={styles.dateLocationBlock}>

                <LocationSelectBox/>

            </div>

        </div>
    )

    render() {
        return (
            <>

                {this.locationSelectRow()}

                <div className="missing-shipments-container">
                    <Typography variant="h6" component="div" gutterBottom className="form-header">
                    </Typography>
                    <div className="missing-labels-table-content">
                        <TableContainer>
                            <Table size={"medium"} stickyHeader aria-label="sticky table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell className='cellPadding' key="brand">
                                            <TableSortLabel
                                                active={this.state.valueToOrderByShipmentsSummary === "brand"}
                                                direction={this.state.valueToOrderByShipmentsSummary ? this.state.orderDirectionShipmentsSummary : "asc"}
                                                onClick={this.createSortHandler("brand")}
                                            >
                                                <p className={styles.cardTotalHeader}>
                                                    Brand
                                                </p>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className='cellPadding' key="productCode">
                                            <TableSortLabel
                                                active={this.state.valueToOrderByShipmentsSummary === "productCode"}
                                                direction={this.state.valueToOrderByShipmentsSummary ? this.state.orderDirectionShipmentsSummary : "asc"}
                                                onClick={this.createSortHandler("productCode")}
                                            >
                                                <p className={styles.cardTotalHeader}>
                                                    Product
                                                </p>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell className='cellPadding' key="printedQty">
                                            <TableSortLabel
                                                active={this.state.valueToOrderByShipmentsSummary === "printedQty"}
                                                direction={this.state.valueToOrderByShipmentsSummary ? this.state.orderDirectionShipmentsSummary : "asc"}
                                                onClick={this.createSortHandler("printedQty")}
                                            >
                                                <p className={styles.cardTotalHeader}>
                                                    # of packages
                                                </p>
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {this.stableSort(this.state.reportData, this.getComparator(this.state.orderDirectionShipmentsSummary, this.state.valueToOrderByShipmentsSummary)).map((report, index) => (
                                        <TableRow hover key={index}>
                                            <TableCell className='cellPadding' style={{ width: "30%" }}>
                                                <p className={styles.cardCellItems}>
                                                    {report.brand}
                                                </p>
                                            </TableCell>
                                            <TableCell className='cellPadding' style={{ width: "30%" }}>
                                                <p className={styles.cardCellItems}>
                                                    {report.productCode}
                                                </p>
                                            </TableCell>
                                            <TableCell className='cellPadding'>
                                                <p className={styles.cardCellItems}>
                                                    {report.printedQty}
                                                </p>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow hover>
                                        <TableCell className='cellPadding' style={{ width: "30%" }}>
                                            <p className={styles.cardCellItems}>
                                                Total:
                                            </p>
                                        </TableCell>
                                        <TableCell className='cellPadding'/>
                                        <TableCell className='cellPadding'>
                                            <p className={styles.cardCellItems}>
                                                {this.addItUp("printedQty")}
                                            </p>
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
            </>
        )
    }
}

const mapStateToProps = (state) => ({
    userInfo: userInfoSelector(state),
    selectedLocationId: selectedLocationIdSelector(state),
})

const actionCreators =
{
    updateUserInfo,
}

ShipmentsSummary.propTypes = {
    selectedLocation: PropTypes.object,
}

ShipmentsSummary.defaultProps = {
    rows: []
}

export default withShipment(
    {
        mapStateToProps,
        actionCreators
    }
    , ShipmentsSummary);